import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '@/store/store';
import type { FilterSelection } from '@interfaces/filters/domain';
import { filtersAdapter } from '@/store/slices/filters/filtersSlice';

// Local, cycle-free read of the filters catalog. Deliberately NOT imported
// from '@/store/slices/filters/selectors' — that module's top-level
// `createSelector` calls depend on `selectSelectedMarketIds` from THIS file,
// so importing back from it here creates a real ESM circular-dependency
// deadlock (the cross-imported selector resolves to `undefined` at
// module-eval time, and `createSelector` throws). Reading the catalog via
// the exported entity adapter instead has no dependency back on this file.
const selectCatalogFilters = filtersAdapter.getSelectors(
  (s: RootState) => s.activeGroup.filters.filters,
).selectAll;

export const selectSelectedMarketIds = (s: RootState): number[] =>
  s.userSelection.markets ?? [];

// Locates the RANGE-kind filter under the "Age" category.
// Assumes bootstrap emits exactly one RANGE-kind filter (Age). Update to a
// role/type lookup if that assumption ever changes.
export const selectAgeFilterCode = createSelector(
  [selectCatalogFilters],
  (filters) => filters.find((f) => f.kind === 'RANGE')?.code,
);

// Draft-ready selections merge the transitional `userSelection.ageRange`
// top-level field into FilterSelection[]. TODO: delete this selector when
// userSelection.ageRange is retired.
export const selectDraftReadyFilterSelections = createSelector(
  [
    (s: RootState) => (s.userSelection.filters ?? []) as unknown as FilterSelection[],
    (s: RootState) => s.userSelection.ageRange as [number, number] | null | undefined,
    selectAgeFilterCode,
  ],
  (filters, ageRange, ageCode): FilterSelection[] => {
    if (!ageRange || !ageCode) return filters;
    if (filters.some((f) => f.code === ageCode)) return filters;
    return [
      ...filters,
      { code: ageCode, kind: 'RANGE', range: { min: ageRange[0], max: ageRange[1] } },
    ];
  },
);

// Legacy shim: flatten FilterSelection[] → number[] (option ids) for legacy
// consumers (SaveView, FileDownload, useUnifiedPayload, legacy dataRequest
// helpers). TODO: delete when all legacy consumers migrate to V2 endpoints
// and read FilterSelection[] directly.
export const selectLegacyFilterOptionIdArray = createSelector(
  [selectDraftReadyFilterSelections, selectCatalogFilters],
  (selections, catalogFilters) => {
    const out: number[] = [];
    for (const sel of selections) {
      if (sel.kind !== 'OPTION') continue;
      const filter = catalogFilters.find((f) => f.code === sel.code);
      if (!filter) continue;
      for (const optCode of sel.options) {
        const opt = filter.options.find((o) => o.code === optCode);
        if (opt) out.push(opt.id);
      }
    }
    return out;
  },
);
